import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { landingForUser } from "@/lib/roleRedirect";

export function ProtectedRoute({ children, requireAdmin = false, requireLabStaff = false, requirePartner = false, fieldLogin = false, fieldRoles = null }) {
  const { user, loading } = useAuth();
  // Router location (not window.location) — during navigation transitions
  // window.location already points at the NEXT url, which produced wrong
  // `next` params (e.g. logout landing on /login?next=%2F).
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="flex items-center gap-3 text-slate-500">
          <div className="w-5 h-5 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
          Loading...
        </div>
      </div>
    );
  }

  if (!user) {
    // Field routes bounce to the technician magic-link login and return
    // to the exact job link after verification (SMS deep-link ready).
    if (fieldLogin) {
      const next = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/field/login?next=${next}`} replace />;
    }
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  }
  // Nov 2026 — Tank Water Health Check is AWARE-only (the backend
  // narrowed OFFICE_ROLES to admin/lab_technician). `fieldRoles` locks
  // the matching route out for any other signed-in role (e.g. a trade
  // partner) instead of letting the page load and 403 on every API call.
  if (fieldLogin && fieldRoles && !fieldRoles.includes(user.role)) {
    return <Navigate to={landingForUser(user)} replace />;
  }
  // Admin-only route accessed by a lab tech → send them to /admin/lab
  // (anyone else → their role's landing page)
  if (requireAdmin && user.role !== "admin") return <Navigate to={landingForUser(user)} replace />;
  if (requireLabStaff && !["admin", "lab_technician"].includes(user.role)) {
    return <Navigate to={landingForUser(user)} replace />;
  }
  // Aware Partner Network Phase 5 — partner-only route. Admins are
  // allowed through for preview/impersonation; everyone else lands on
  // their own role's home page.
  if (requirePartner && !["admin", "partner"].includes(user.role)) {
    return <Navigate to={landingForUser(user)} replace />;
  }
  if (!requireAdmin && !requireLabStaff && !requirePartner && user.status !== "approved" && user.role !== "admin") {
    return <Navigate to="/trade/pending" replace />;
  }
  return children;
}
